import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import api from "@/lib/api";
import {
  PresenceRegistrationsTable,
  PresencePlanningView,
  PresenceEmailModal,
  PresenceCheckinSheet,
  PresencePreRegistrations,
} from "./PresenceWidgets";
import MembersTab from "./MembersTab";
import LogisticsTab from "./LogisticsTab";
import DonationsTab from "./DonationsTab";
import PartnersTab from "./PartnersTab";
import IntervenantsTab from "./IntervenantsTab";
import SauvegardeTab from "./SauvegardeTab";
import DocumentsTab from "./DocumentsTab";
import PlanningTab from "./PlanningTab";
import RisksTab from "./RisksTab";
import FinanceTab from "./FinanceTab";
import AdminsTab from "./AdminsTab";
import AccountsTab from "./AccountsTab";
import PresenceScheduleTab from "./PresenceScheduleTab";
import PresencePricingTab from "./PresencePricingTab";
import PhotosTab from "./PhotosTab";
import VideosTab from "./VideosTab";
import CommunicationTab from "./CommunicationTab";
import PushBroadcastTab from "./PushBroadcastTab";
import BeaconTab from "./BeaconTab";
import VivaArtistTab from "./VivaArtistTab";
import PrepDashboardTab from "./PrepDashboardTab";
import ComptabiliteTab from "./ComptabiliteTab";
import HospitaliteTab from "./HospitaliteTab";
import AdminQuickPulse from "./AdminQuickPulse";
import RetranslateButton from "./RetranslateButton";
import NewsletterTab from "./NewsletterTab";
import RestaurationTab from "./RestaurationTab";
import RHTab from "./RHTab";
import HebergementTab from "./HebergementTab";

const emptyEvent = () => ({
  title: { fr: "", en: "" }, description: { fr: "", en: "" },
  date: "", end_date: "", time: "", location: { fr: "", en: "" },
  image_url: "", ticketed: false, price_eur: 0, capacity: 0, published: true,
  recurrence: "none", recurrence_until: "",
  tags: [],
  category: "autre", sub_category: "",
});
const emptyArticle = () => ({
  title: { fr: "", en: "" }, body: { fr: "", en: "" },
  image_url: "", author: "", date: "", published: true,
});

function Stats({ t, locale, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [showFutureEvents, setShowFutureEvents] = useState(false);
  const [futureEvents, setFutureEvents] = useState([]);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  const loadFuture = async () => {
    if (futureEvents.length > 0) { setShowFutureEvents((v) => !v); return; }
    try {
      const { data } = await api.get("/events");
      const now = new Date();
      const list = (Array.isArray(data) ? data : data.items || [])
        .filter((e) => e.date && new Date(e.date) >= now)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setFutureEvents(list);
    } catch { /* ignore */ }
    setShowFutureEvents(true);
  };
  if (!stats) return null;
  const cards = [
    { label: t.admin.stats.events, value: stats.events, key: "events", clickable: true, onClick: loadFuture },
    { label: locale === "fr" ? "Documents" : "Documents", value: stats.documents, key: "documents", clickable: true, onClick: () => onNavigate?.("documents") },
    { label: locale === "fr" ? "Membres" : "Members", value: stats.members, key: "members", clickable: true, onClick: () => onNavigate?.("presence") },
    { label: t.admin.stats.articles, value: stats.articles, key: "articles" },
    { label: t.admin.stats.unread, value: stats.messages_unread, key: "messages", clickable: true, onClick: () => onNavigate?.("messages") },
    { label: t.admin.stats.orders, value: stats.orders_paid, key: "orders", clickable: true, onClick: () => onNavigate?.("orders") },
    { label: t.admin.stats.revenue, value: `${stats.revenue_eur.toFixed(2)} €`, key: "revenue" },
  ];
  return (
    <div className="space-y-6">
      <AdminQuickPulse onNavigate={onNavigate} />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {cards.map((c) => (
          <div
            key={c.label}
            className="admin-card text-center"
            data-testid={`stat-${c.key}`}
            onClick={c.onClick}
            style={c.clickable ? { cursor: "pointer", transition: "all 0.2s" } : undefined}
            onMouseEnter={(e) => { if (c.clickable) { e.currentTarget.style.borderColor = "var(--cinnabar)"; e.currentTarget.style.transform = "translateY(-2px)"; } }}
            onMouseLeave={(e) => { if (c.clickable) { e.currentTarget.style.borderColor = ""; e.currentTarget.style.transform = ""; } }}
          >
            <div className="label-caps mb-2" style={{ color: "var(--muted)" }}>{c.label}</div>
            <div className="font-display text-3xl">{c.value}</div>
            {c.clickable && <div className="font-caps text-[0.55rem] mt-2" style={{ color: "var(--cinnabar)", letterSpacing: "0.18em" }}>↓ VOIR</div>}
          </div>
        ))}
      </div>
      {showFutureEvents && (
        <div className="admin-card" data-testid="future-events-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-xl">Événements à venir <span className="font-caps text-[0.7rem]" style={{ color: "var(--muted)" }}>{futureEvents.length}</span></div>
            <button className="btn btn-outline btn-xs" onClick={() => setShowFutureEvents(false)} data-testid="future-events-close">Fermer</button>
          </div>
          {futureEvents.length === 0 ? (
            <div className="font-body text-sm italic" style={{ color: "var(--muted)" }}>Aucun événement programmé.</div>
          ) : (
            <div className="space-y-2">
              {futureEvents.map((e) => (
                <div key={e.id} className="flex items-center gap-4 p-3 border" style={{ borderColor: "var(--border)" }} data-testid={`future-event-${e.id}`}>
                  <div className="font-caps text-[0.65rem]" style={{ color: "var(--cinnabar)", letterSpacing: "0.15em", minWidth: 110 }}>
                    {new Date(e.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                  <div className="flex-1 font-body text-base">{e.title?.fr || e.title?.en}</div>
                  {e.location && <div className="font-body text-xs" style={{ color: "var(--muted)" }}>{e.location}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EventEditor({ initial, onCancel, onSaved, t, locale }) {
  const [e, setE] = useState(initial || emptyEvent());
  const set = (path, val) => setE((s) => {
    const next = { ...s };
    if (Array.isArray(path)) {
      next[path[0]] = { ...next[path[0]], [path[1]]: val };
    } else next[path] = val;
    return next;
  });
  const save = async () => {
    if (initial?.id) {
      const { data } = await api.put(`/admin/events/${initial.id}`, e);
      onSaved(data);
    } else {
      const { data } = await api.post("/admin/events", e);
      onSaved(data);
    }
  };
  return (
    <div className="admin-card space-y-4" data-testid="event-editor">
      <div className="p-3" style={{ backgroundColor: "var(--surface-2)", borderLeft: "3px solid var(--gold)" }}>
        <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--gold)" }}>
          ◇ {locale === "fr" ? "TRADUCTION AUTO (EN · IT · ES · DE)" : "AUTO-TRANSLATION (EN · IT · ES · DE)"} ◇
        </span>
        <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>
          {locale === "fr"
            ? "Saisissez uniquement en français. Les traductions vers EN/IT/ES/DE sont générées automatiquement par IA à l'enregistrement."
            : "Enter content in French only. EN/IT/ES/DE translations are generated automatically on save."}
        </p>
      </div>
      <div>
        <label className="label-caps">{t.admin.eventFields.titleFr}</label>
        <input className="input-classic" value={e.title.fr} onChange={(ev) => set(["title", "fr"], ev.target.value)} data-testid="event-title-fr" />
      </div>
      <div>
        <label className="label-caps">{t.admin.eventFields.descFr}</label>
        <textarea rows={3} className="input-classic" value={e.description.fr}
                  onChange={(ev) => set(["description", "fr"], ev.target.value)} data-testid="event-desc-fr" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label-caps">{locale === "fr" ? "Catégorie" : "Category"}</label>
          <select className="input-classic" value={e.category || "autre"}
                  onChange={(ev) => set("category", ev.target.value)}
                  data-testid="event-category">
            <option value="office">{locale === "fr" ? "Office (vêpres, messe, adoration…)" : "Office (vespers, mass, adoration…)"}</option>
            <option value="concert">{locale === "fr" ? "Concert" : "Concert"}</option>
            <option value="conference">{locale === "fr" ? "Conférence" : "Lecture"}</option>
            <option value="autre">{locale === "fr" ? "Autre" : "Other"}</option>
          </select>
        </div>
        {e.category === "office" && (
          <div>
            <label className="label-caps">{locale === "fr" ? "Type d'office" : "Office type"}</label>
            <select className="input-classic" value={e.sub_category || ""}
                    onChange={(ev) => set("sub_category", ev.target.value)}
                    data-testid="event-sub-category">
              <option value="">{locale === "fr" ? "— choisir —" : "— choose —"}</option>
              <option value="vepres">{locale === "fr" ? "Vêpres" : "Vespers"}</option>
              <option value="messe">{locale === "fr" ? "Messe" : "Mass"}</option>
              <option value="adoration">{locale === "fr" ? "Adoration" : "Adoration"}</option>
              <option value="complies">{locale === "fr" ? "Complies" : "Compline"}</option>
              <option value="laudes">{locale === "fr" ? "Laudes" : "Lauds"}</option>
              <option value="autre">{locale === "fr" ? "Autre" : "Other"}</option>
            </select>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="label-caps">{t.admin.eventFields.date}</label>
          <input type="date" className="input-classic" value={e.date} onChange={(ev) => set("date", ev.target.value)} />
        </div>
        <div>
          <label className="label-caps">{locale === "fr" ? "Date de fin (multi-jours)" : "End date (multi-day)"}</label>
          <input type="date" className="input-classic" value={e.end_date || ""}
                 onChange={(ev) => set("end_date", ev.target.value)}
                 data-testid="event-end-date" />
        </div>
        <div>
          <label className="label-caps">{t.admin.eventFields.time}</label>
          <input className="input-classic" value={e.time} onChange={(ev) => set("time", ev.target.value)} placeholder="20:30" />
        </div>
        <div>
          <label className="label-caps">{locale === "fr" ? "Récurrence" : "Recurrence"}</label>
          <select className="input-classic" value={e.recurrence || "none"}
                  onChange={(ev) => set("recurrence", ev.target.value)}
                  data-testid="event-recurrence">
            <option value="none">{locale === "fr" ? "Aucune (date unique)" : "None (single date)"}</option>
            <option value="daily">{locale === "fr" ? "Quotidienne" : "Daily"}</option>
            <option value="weekly">{locale === "fr" ? "Hebdomadaire" : "Weekly"}</option>
          </select>
        </div>
      </div>
      {e.recurrence && e.recurrence !== "none" && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="label-caps">{locale === "fr" ? "Récurrence jusqu'au" : "Recurrence until"}</label>
            <input type="date" className="input-classic" value={e.recurrence_until || ""}
                   onChange={(ev) => set("recurrence_until", ev.target.value)}
                   data-testid="event-recurrence-until" />
          </div>
        </div>
      )}
      <div>
        <label className="label-caps">{t.admin.eventFields.locFr}</label>
        <input className="input-classic" value={e.location.fr} onChange={(ev) => set(["location", "fr"], ev.target.value)} data-testid="event-loc-fr" />
      </div>
      <div>
        <label className="label-caps">{t.admin.eventFields.image}</label>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start mt-1">
          <input className="input-classic" value={e.image_url}
                 onChange={(ev) => set("image_url", ev.target.value)}
                 placeholder="https://… ou /uploads/…" />
          <label className="btn btn-outline cursor-pointer" data-testid="event-image-upload-label">
            {t.admin.uploadImage || "Téléverser…"}
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" className="hidden"
                   data-testid="event-image-upload"
                   onChange={async (ev) => {
                     const file = ev.target.files?.[0];
                     if (!file) return;
                     const fd = new FormData();
                     fd.append("file", file);
                     try {
                       const { data } = await api.post("/admin/uploads/image", fd, {
                         headers: { "Content-Type": "multipart/form-data" },
                       });
                       set("image_url", data.url);
                     } catch (err) {
                       window.alert(err?.response?.data?.detail || "Erreur upload");
                     } finally {
                       ev.target.value = "";
                     }
                   }} />
          </label>
        </div>
        {e.image_url && (
          <div className="mt-3">
            <img src={e.image_url} alt="" className="h-32 max-w-full object-cover border" style={{ borderColor: "var(--border)" }} />
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox" checked={e.ticketed} onChange={(ev) => set("ticketed", ev.target.checked)} />
          {t.admin.eventFields.ticketed}
        </label>
        <div>
          <label className="label-caps">{t.admin.eventFields.price}</label>
          <input type="number" step="0.5" className="input-classic" value={e.price_eur}
                 onChange={(ev) => set("price_eur", parseFloat(ev.target.value || 0))} />
        </div>
        <div>
          <label className="label-caps">{t.admin.eventFields.capacity}</label>
          <input type="number" className="input-classic" value={e.capacity}
                 onChange={(ev) => set("capacity", parseInt(ev.target.value || 0, 10))} />
        </div>
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox" checked={e.published} onChange={(ev) => set("published", ev.target.checked)} />
          {t.admin.eventFields.published}
        </label>
      </div>
      {/* Tags — used to link this event to thematic programs (Fêtes 2027 unified programme) */}
      <div className="p-3" style={{ background: "var(--surface-2)", borderLeft: "3px solid var(--cinnabar)" }}>
        <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--cinnabar)" }}>
          ◆ {locale === "fr" ? "TAGS — PROGRAMMES" : "TAGS — PROGRAMS"}
        </span>
        <p className="text-xs mt-1 mb-3" style={{ color: "var(--ink-2)" }}>
          {locale === "fr"
            ? "Cochez « Fêtes 2027 » pour que cet événement apparaisse dans le programme officiel des Fêtes de saint Léonard."
            : "Tick \"Feasts 2027\" to add this event to the official Fêtes de saint Léonard programme."}
        </p>
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox"
                 data-testid="event-tag-fetes-2027"
                 checked={(e.tags || []).includes("fetes_2027")}
                 onChange={(ev) => {
                   const cur = new Set(e.tags || []);
                   if (ev.target.checked) cur.add("fetes_2027"); else cur.delete("fetes_2027");
                   set("tags", Array.from(cur));
                 }} />
          <span className="font-caps text-[0.7rem]" style={{ letterSpacing: "0.18em" }}>
            ✦ {locale === "fr" ? "FÊTES DE SAINT LÉONARD 2027" : "FÊTES DE SAINT LÉONARD 2027"}
          </span>
        </label>
      </div>
      <div className="flex gap-3">
        <button onClick={save} data-testid="event-editor-save" className="btn btn-primary">{t.admin.save}</button>
        <button onClick={onCancel} className="btn btn-ghost">{t.admin.cancel}</button>
      </div>
    </div>
  );
}

function ArticleEditor({ initial, onCancel, onSaved, t }) {
  const [a, setA] = useState(initial || emptyArticle());
  const set = (path, val) => setA((s) => {
    const next = { ...s };
    if (Array.isArray(path)) next[path[0]] = { ...next[path[0]], [path[1]]: val };
    else next[path] = val;
    return next;
  });
  const save = async () => {
    if (initial?.id) {
      const { data } = await api.put(`/admin/articles/${initial.id}`, a);
      onSaved(data);
    } else {
      const { data } = await api.post("/admin/articles", a);
      onSaved(data);
    }
  };
  return (
    <div className="admin-card space-y-4" data-testid="article-editor">
      <div className="p-3" style={{ backgroundColor: "var(--surface-2)", borderLeft: "3px solid var(--gold)" }}>
        <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--gold)" }}>
          ◇ TRADUCTION AUTO (EN · IT · ES · DE) ◇
        </span>
        <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>
          Saisissez uniquement en français. Les traductions vers EN/IT/ES/DE sont générées automatiquement par IA à l&apos;enregistrement.
        </p>
      </div>
      <div>
        <label className="label-caps">{t.admin.articleFields.titleFr}</label>
        <input className="input-classic" value={a.title.fr} onChange={(ev) => set(["title", "fr"], ev.target.value)} data-testid="article-title-fr" />
      </div>
      <div>
        <label className="label-caps">{t.admin.articleFields.bodyFr}</label>
        <textarea rows={6} className="input-classic" value={a.body.fr}
                  onChange={(ev) => set(["body", "fr"], ev.target.value)} data-testid="article-body-fr" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="label-caps">{t.admin.articleFields.author}</label>
          <input className="input-classic" value={a.author} onChange={(ev) => set("author", ev.target.value)} />
        </div>
        <div>
          <label className="label-caps">{t.admin.articleFields.date}</label>
          <input type="date" className="input-classic" value={a.date} onChange={(ev) => set("date", ev.target.value)} />
        </div>
        <label className="flex items-center gap-2 font-body">
          <input type="checkbox" checked={a.published} onChange={(ev) => set("published", ev.target.checked)} />
          {t.admin.articleFields.published}
        </label>
      </div>
      <div>
        <label className="label-caps">{t.admin.articleFields.image}</label>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start mt-1">
          <input className="input-classic" value={a.image_url}
                 onChange={(ev) => set("image_url", ev.target.value)}
                 placeholder="https://… ou /uploads/…" />
          <label className="btn btn-outline cursor-pointer" data-testid="article-image-upload-label">
            {t.admin.uploadImage || "Téléverser…"}
            <input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" className="hidden"
                   data-testid="article-image-upload"
                   onChange={async (ev) => {
                     const file = ev.target.files?.[0];
                     if (!file) return;
                     const fd = new FormData();
                     fd.append("file", file);
                     try {
                       const { data } = await api.post("/admin/uploads/image", fd, {
                         headers: { "Content-Type": "multipart/form-data" },
                       });
                       set("image_url", data.url);
                     } catch (err) {
                       window.alert(err?.response?.data?.detail || "Erreur upload");
                     } finally {
                       ev.target.value = "";
                     }
                   }} />
          </label>
        </div>
        {a.image_url && (
          <div className="mt-3">
            <img src={a.image_url} alt="" className="h-32 max-w-full object-cover border" style={{ borderColor: "var(--border)" }} />
          </div>
        )}
      </div>
      <div className="flex gap-3">
        <button onClick={save} data-testid="article-editor-save" className="btn btn-primary">{t.admin.save}</button>
        <button onClick={onCancel} className="btn btn-ghost">{t.admin.cancel}</button>
      </div>
    </div>
  );
}

function EventsTab({ t, locale }) {
  const isFr = locale === "fr";
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // null = none, 'new' = new, object = edit
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState("");
  const load = () => api.get("/admin/events").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await api.delete(`/admin/events/${id}`);
    load();
  };

  const exportJson = async () => {
    setImportError("");
    try {
      const res = await api.get("/admin/events/export");
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `events-${ts}.json`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (ex) {
      setImportError(ex?.response?.data?.detail || "Export error");
    }
  };

  const importJson = async (file) => {
    setImportBusy(true); setImportError(""); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("by", "id");
      fd.append("wipe", "false");
      const { data } = await api.post("/admin/events/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      load();
    } catch (ex) {
      setImportError(ex?.response?.data?.detail || "Import error");
    } finally { setImportBusy(false); }
  };

  return (
    <div>
      {editing === null && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <button data-testid="admin-new-event" onClick={() => setEditing("new")} className="btn btn-primary">
            + {t.admin.newEvent}
          </button>
          <RetranslateButton collection="events" label="événements" />
        </div>
      )}
      {editing === "new" && (
        <EventEditor t={t} locale={locale} onCancel={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}
      {editing && typeof editing === "object" && (
        <EventEditor initial={editing} t={t} locale={locale} onCancel={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}

      {editing === null && (
        <>
          {/* ---- Migration JSON ---- */}
          <div className="bg-white border p-4 sm:p-5 mb-4" style={{ borderColor: "var(--border)" }} data-testid="events-migration">
            <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
              <div>
                <div className="label-caps">{isFr ? "MIGRATION JSON" : "JSON MIGRATION"}</div>
                <p className="font-body text-xs mt-1" style={{ color: "var(--muted)" }}>
                  {isFr
                    ? "Export du programme complet vers JSON (sauvegarde, migration preview → production). Import upsert par `id` — non destructif."
                    : "Export the full programme to JSON. Import upserts by `id` — non destructive."}
                </p>
              </div>
              <div className="flex gap-3 flex-wrap items-center">
                <button type="button"
                        data-testid="events-export-btn"
                        onClick={exportJson}
                        className="font-caps text-[0.62rem] px-3 py-2 border"
                        style={{ borderColor: "var(--lapis)", color: "var(--lapis)", letterSpacing: "0.2em" }}>
                  ⇩ {isFr ? "EXPORTER JSON" : "EXPORT JSON"}
                </button>
                <label className="font-caps text-[0.62rem] px-3 py-2 border cursor-pointer"
                       style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.2em" }}>
                  ⇧ {isFr ? "IMPORTER JSON" : "IMPORT JSON"}
                  <input data-testid="events-import-file" type="file" accept="application/json,.json"
                         className="hidden" disabled={importBusy}
                         onChange={(e) => {
                           const f = e.target.files?.[0];
                           if (f) importJson(f);
                           e.target.value = "";
                         }} />
                </label>
              </div>
            </div>
            {importBusy && (
              <p className="font-body italic text-sm mt-2" style={{ color: "var(--muted)" }}>
                {isFr ? "Import en cours…" : "Importing…"}
              </p>
            )}
            {importError && (
              <p className="font-body text-sm mt-2" style={{ color: "var(--cinnabar)" }} data-testid="events-import-error">
                {importError}
              </p>
            )}
            {importResult && (
              <div className="font-body text-sm mt-3 p-3 border-l-2"
                   style={{ borderColor: "var(--gold)", background: "var(--parchment-2)" }}
                   data-testid="events-import-result">
                <div className="font-caps text-[0.62rem]" style={{ color: "var(--gold)", letterSpacing: "0.2em" }}>
                  {isFr ? "IMPORT TERMINÉ" : "IMPORT DONE"}
                </div>
                <div className="mt-1">
                  <strong>{importResult.inserted}</strong> {isFr ? "ajouté(s)" : "added"} ·{" "}
                  <strong>{importResult.updated}</strong> {isFr ? "mis à jour" : "updated"} ·{" "}
                  <strong>{importResult.skipped}</strong> {isFr ? "ignoré(s)" : "skipped"} ·{" "}
                  {isFr ? "total" : "total"} {importResult.total}
                </div>
                {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
                  <ul className="mt-2 list-disc pl-5" style={{ color: "var(--cinnabar)" }}>
                    {importResult.errors.map((er, i) => <li key={i}>{er}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3 mt-6">
          {list.map((e) => (
            <div key={e.id} data-testid={`admin-event-${e.id}`} className="admin-card flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {e.image_url && (
                  <img src={e.image_url} alt="" className="h-16 w-20 object-cover border shrink-0" style={{ borderColor: "var(--border)" }} />
                )}
                <div className="min-w-0">
                  <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)" }}>
                    {new Date(e.date).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB")}
                    {e.time ? ` · ${e.time}` : ""} {e.published ? "" : " · (draft)"}
                    {e.category && (
                      <span className="ml-2 px-2 py-0.5 border" style={{ borderColor: "var(--gold)", color: "var(--gold)" }}>
                        {({
                          office: locale === "fr" ? "Office" : "Office",
                          concert: locale === "fr" ? "Concert" : "Concert",
                          conference: locale === "fr" ? "Conférence" : "Lecture",
                          autre: locale === "fr" ? "Autre" : "Other",
                        }[e.category] || e.category)}
                        {e.category === "office" && e.sub_category ? ` · ${e.sub_category}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="font-display text-xl">{pickLocalized(e.title, locale)}</div>
                  <div className="font-body text-sm" style={{ color: "var(--muted)" }}>
                    {e.ticketed ? `${e.price_eur} € · ${e.capacity || "∞"} places` : "—"}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(e)} className="btn btn-outline">{t.admin.edit}</button>
                <button onClick={() => del(e.id)} className="btn btn-ghost" style={{ color: "var(--cinnabar)" }}>
                  {t.admin.delete}
                </button>
              </div>
            </div>
          ))}
          </div>
        </>
      )}
    </div>
  );
}

function ArticlesTab({ t, locale }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/articles").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await api.delete(`/admin/articles/${id}`);
    load();
  };
  return (
    <div>
      {editing === null && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <button data-testid="admin-new-article" onClick={() => setEditing("new")} className="btn btn-primary">
            + {t.admin.newArticle}
          </button>
          <RetranslateButton collection="articles" label="articles" />
        </div>
      )}
      {editing === "new" && (
        <ArticleEditor t={t} onCancel={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}
      {editing && typeof editing === "object" && (
        <ArticleEditor initial={editing} t={t} onCancel={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}
      {editing === null && (
        <div className="space-y-3 mt-6">
          {list.map((a) => (
            <div key={a.id} data-testid={`admin-article-${a.id}`}
                 className="admin-card flex items-center justify-between gap-4">
              <div>
                <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)" }}>
                  {a.author} · {new Date(a.date).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB")}
                  {a.published ? "" : " · (draft)"}
                </div>
                <div className="font-display text-xl">{pickLocalized(a.title, locale)}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(a)} className="btn btn-outline">{t.admin.edit}</button>
                <button onClick={() => del(a.id)} className="btn btn-ghost" style={{ color: "var(--cinnabar)" }}>
                  {t.admin.delete}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MessagesTab({ t, locale }) {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/messages").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/admin/messages/${id}/read`); load(); };
  if (list.length === 0) {
    return <p className="font-body italic" style={{ color: "var(--muted)" }}>{t.admin.noMessages}</p>;
  }
  return (
    <div className="space-y-3">
      {list.map((m) => (
        <div key={m.id} data-testid={`admin-message-${m.id}`} className="admin-card">
          <div className="flex items-start justify-between mb-2 gap-4">
            <div>
              <div className="font-display text-lg">{m.name} <span style={{ color: "var(--muted)" }}>·</span> {m.email}</div>
              {m.phone && <div className="font-body text-sm" style={{ color: "var(--muted)" }}>{m.phone}</div>}
              <div className="font-caps text-[0.62rem] mt-1" style={{ color: "var(--gold)" }}>
                {new Date(m.created_at).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
                {m.subject ? ` · ${m.subject}` : ""}
              </div>
            </div>
            {!m.read && (
              <button onClick={() => markRead(m.id)} className="btn btn-ghost">
                {t.admin.markRead}
              </button>
            )}
          </div>
          <p className="font-body whitespace-pre-line">{m.message}</p>
        </div>
      ))}
    </div>
  );
}

function OrdersTab({ t, locale }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setList(r.data)); }, []);
  if (list.length === 0) {
    return <p className="font-body italic" style={{ color: "var(--muted)" }}>{t.admin.noOrders}</p>;
  }
  const dayLabels = {
    "2027-07-02": "Vendredi 2 (Libération)",
    "2027-07-03": "Samedi 3 (Lumière)",
    "2027-07-04": "Dimanche 4 (Unité)",
  };
  const formulaLabels = {
    jour: "Journée découverte",
    pass3j: "Pass 3 jours",
    pass3j_repas: "Pass 3 jours + repas",
    solidaire: "Tarif solidaire",
  };
  const workshopLabels = {
    icone: "Icône", sculpture: "Sculpture", chants: "Chants", botanique: "Botanique",
  };
  const kindColors = {
    presence: "var(--lapis)",
    membership: "var(--moss)",
    donation: "var(--cinnabar)",
    event: "var(--gold)",
  };
  return (
    <div className="space-y-3">
      {list.map((o) => (
        <div key={o.session_id} className="admin-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="tag" style={{ color: kindColors[o.kind], borderColor: kindColors[o.kind] }}>
                  {o.kind}
                </span>
                <span className="font-caps text-[0.62rem]" style={{ color: "var(--muted)" }}>
                  {new Date(o.created_at).toLocaleString(locale === "fr" ? "fr-FR" : "en-GB")}
                </span>
              </div>
              <div className="font-display text-lg">
                {o.name} <span style={{ color: "var(--muted)" }}>·</span> {o.email}
                {o.phone ? <span className="font-body text-sm" style={{ color: "var(--muted)" }}> · {o.phone}</span> : null}
              </div>

              {o.kind === "presence" && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 font-body text-sm">
                  <div><span style={{ color: "var(--muted)" }}>Formule :</span> <strong>{formulaLabels[o.formula] || o.formula}</strong> × {o.quantity}</div>
                  {o.day_choice && <div><span style={{ color: "var(--muted)" }}>Journée :</span> <strong>{dayLabels[o.day_choice] || o.day_choice}</strong></div>}
                  {o.workshop && <div><span style={{ color: "var(--muted)" }}>Atelier :</span> <strong>{workshopLabels[o.workshop] || o.workshop}</strong></div>}
                  {o.notes && <div className="sm:col-span-2"><span style={{ color: "var(--muted)" }}>Notes :</span> <em>{o.notes}</em></div>}
                </div>
              )}
              {o.kind === "event" && o.event_title && (
                <div className="font-body text-sm mt-1">{pickLocalized(o.event_title, locale)} × {o.quantity}</div>
              )}
              {o.kind === "membership" && (
                <div className="font-body text-sm mt-1">Adhésion <strong>{o.tier}</strong></div>
              )}
              {o.kind === "donation" && (
                <div className="font-body text-sm mt-1">Projet : <strong>{o.project_id}</strong></div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-display text-2xl">{o.amount?.toFixed(2)} €</div>
              <div className="font-caps text-[0.62rem]" style={{
                color: o.payment_status === "paid" ? "var(--moss)" : "var(--muted)"
              }}>
                {o.payment_status}
              </div>
              {o.kind === "presence" && o.payment_status === "paid" && (
                <button
                  data-testid={`resend-ticket-${o.session_id}`}
                  className="btn btn-outline btn-sm mt-2"
                  onClick={async () => {
                    if (!window.confirm(locale === "fr"
                      ? `Renvoyer le billet QR à ${o.email} ?`
                      : `Resend QR ticket to ${o.email}?`)) return;
                    try {
                      await api.post(`/admin/presence/transactions/${o.session_id}/resend-ticket`);
                      alert(locale === "fr" ? "✓ Billet renvoyé" : "✓ Ticket sent");
                    } catch (e) {
                      alert((locale === "fr" ? "Erreur : " : "Error: ") + (e.response?.data?.detail || e.message));
                    }
                  }}>
                  ✉ {locale === "fr" ? "Renvoyer billet" : "Resend ticket"}
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PresenceTab({ t, locale }) {
  const [view, setView] = useState("registrations"); // registrations | planning | sheet
  const [data, setData] = useState(null);
  const [nlStats, setNlStats] = useState({ total: 0, active: 0 });
  const [formula, setFormula] = useState("all");
  const [day, setDay] = useState("all");
  const [status, setStatus] = useState("all");
  const [emailOpen, setEmailOpen] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (formula !== "all") params.set("formula", formula);
    if (day !== "all") params.set("day", day);
    if (status !== "all") params.set("status", status);
    api.get(`/admin/presence/registrations?${params.toString()}`).then((r) => setData(r.data));
    api.get("/admin/newsletter/subscribers").then((r) => setNlStats({
      total: r.data.total || 0, active: r.data.active || 0,
    })).catch(() => {});
  };
  useEffect(load, [formula, day, status]); // eslint-disable-line

  const onItemUpdated = (updated) => {
    setData((d) => d ? {
      ...d,
      items: d.items.map((it) => it.session_id === updated.session_id ? updated : it),
    } : d);
  };

  const dayLabels = {
    "2027-07-02": "Vendredi 2 (Libération)",
    "2027-07-03": "Samedi 3 (Lumière)",
    "2027-07-04": "Dimanche 4 (Unité)",
  };
  const formulaLabels = {
    jour: "Journée découverte",
    pass3j: "Pass 3 jours",
    pass3j_repas: "Pass 3 jours + repas",
    solidaire: "Tarif solidaire",
  };

  const downloadCsv = async () => {
    const params = new URLSearchParams();
    if (formula !== "all") params.set("formula", formula);
    if (day !== "all") params.set("day", day);
    if (status !== "all") params.set("status", status);
    const url = `${api.defaults.baseURL}/admin/presence/export.csv?${params.toString()}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) { alert("Erreur export CSV"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    const objUrl = URL.createObjectURL(blob);
    a.href = objUrl;
    a.download = res.headers.get("Content-Disposition")?.match(/filename="([^"]+)"/)?.[1] || "presence.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objUrl);
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b print:hidden flex-wrap" style={{ borderColor: "var(--border)" }}>
        <button data-testid="presence-subtab-schedule"
                onClick={() => setView("schedule")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "schedule" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                }}>
          Planning détaillé
        </button>
        <button data-testid="presence-subtab-pricing"
                onClick={() => setView("pricing")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "pricing" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                }}>
          Tarifs
        </button>
        <button data-testid="presence-subtab-registrations"
                onClick={() => setView("registrations")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "registrations" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                }}>
          Inscriptions
        </button>
        <button data-testid="presence-subtab-planning"
                onClick={() => setView("planning")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "planning" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                }}>
          Planning jour-par-jour
        </button>
        <button data-testid="presence-subtab-sheet"
                onClick={() => setView("sheet")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "sheet" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                }}>
          Fiche d&apos;émargement
        </button>
        <button data-testid="presence-subtab-prereg"
                onClick={() => setView("prereg")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "prereg" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                }}>
          Pré-inscriptions
        </button>
        <button data-testid="presence-subtab-members"
                onClick={() => setView("members")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "members" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                }}>
          Membres
        </button>
        <button data-testid="presence-subtab-projet"
                onClick={() => setView("projet")}
                className="nav-link"
                style={{
                  padding: "0.5rem 1.25rem",
                  borderBottom: view === "projet" ? "2px solid var(--cinnabar)" : "2px solid transparent",
                  fontWeight: 600,
                }}>
          Gestion de projet
        </button>
      </div>

      {view === "schedule" ? (
        <PresenceScheduleTab locale={locale} />
      ) : view === "pricing" ? (
        <PresencePricingTab locale={locale} />
      ) : view === "members" ? (
        <MembersTab locale={locale} />
      ) : view === "projet" ? (
        <ProjetHub locale={locale} t={t} />
      ) : view === "planning" ? (
        <PresencePlanningView />
      ) : view === "sheet" ? (
        <PresenceCheckinSheet />
      ) : view === "prereg" ? (
        <PresencePreRegistrations />
      ) : !data ? (
        <p className="font-body" style={{ color: "var(--muted)" }}>{t.common.loading}</p>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="admin-card text-center" data-testid="presence-stat-people">
              <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>Personnes</div>
              <div className="font-display text-3xl">{data.stats.total_people}</div>
            </div>
            <div className="admin-card text-center" data-testid="presence-stat-paid">
              <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>Encaissé</div>
              <div className="font-display text-3xl">{data.stats.total_paid_eur.toFixed(0)} €</div>
            </div>
            <div className="admin-card text-center" data-testid="presence-stat-pending">
              <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>En attente</div>
              <div className="font-display text-3xl">{data.stats.total_pending_eur.toFixed(0)} €</div>
            </div>
            <div className="admin-card text-center" data-testid="presence-stat-meals">
              <div className="label-caps mb-1" style={{ color: "var(--muted)" }}>Repas (3j)</div>
              <div className="font-display text-3xl">{data.stats.meals_count}</div>
            </div>
            {Object.entries(data.stats.by_day).map(([d, n]) => (
              <div key={d} className="admin-card text-center">
                <div className="label-caps mb-1" style={{ color: "var(--gold)" }}>
                  {d === "2027-07-02" ? "Vendredi" : d === "2027-07-03" ? "Samedi" : "Dimanche"}
                </div>
                <div className="font-display text-3xl">{n}</div>
              </div>
            ))}
            <div className="admin-card text-center"
                 data-testid="dashboard-newsletter-kpi"
                 style={{ border: "1px dashed var(--gold)" }}>
              <div className="label-caps mb-1" style={{ color: "var(--gold)" }}>
                ◇ Newsletter
              </div>
              <div className="font-display text-3xl">{nlStats.active}</div>
              <div className="text-[0.6rem]" style={{ color: "var(--muted)" }}>
                / {nlStats.total} {locale === "fr" ? "inscrits" : "subs"}
              </div>
            </div>
          </div>

          {/* Filters + actions */}
          <div className="admin-card flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="label-caps">Formule :</span>
              <select data-testid="presence-filter-formula" value={formula} onChange={(e) => setFormula(e.target.value)}
                      className="input-classic" style={{ background: "transparent", width: "auto" }}>
                <option value="all">Toutes</option>
                {Object.entries(formulaLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="label-caps">Journée :</span>
              <select data-testid="presence-filter-day" value={day} onChange={(e) => setDay(e.target.value)}
                      className="input-classic" style={{ background: "transparent", width: "auto" }}>
                <option value="all">Toutes</option>
                {Object.entries(dayLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="label-caps">Statut :</span>
              <select data-testid="presence-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
                      className="input-classic" style={{ background: "transparent", width: "auto" }}>
                <option value="all">Tous</option>
                <option value="paid">Payés</option>
                <option value="unpaid">Non payés</option>
              </select>
            </div>
            <div className="ml-auto flex gap-2">
              <button data-testid="presence-email-open" onClick={() => setEmailOpen(true)} className="btn btn-outline">
                ✉ E-mail groupé
              </button>
              <button data-testid="presence-export-csv" onClick={downloadCsv} className="btn btn-primary">
                ⬇ Export CSV
              </button>
            </div>
          </div>

          <PresenceRegistrationsTable items={data.items} onItemUpdated={onItemUpdated} />
        </>
      )}

      {emailOpen && (
        <PresenceEmailModal
          filters={{ formula, day, status }}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { t, locale } = useI18n();
  const { admin, logout } = useAuth();
  const [tab, setTab] = useState(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("admin_active_tab") : null;
    return saved || "dashboard";
  });
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("admin_active_tab", tab);
  }, [tab]);
  const navigate = useNavigate();

  if (admin === null) {
    return <div className="container-prieure py-32 text-center font-body" style={{ color: "var(--muted)" }}>{t.common.loading}</div>;
  }
  if (admin === false) {
    navigate("/connexion", { replace: true });
    return null;
  }

  const tabs = [
    { key: "dashboard", label: t.admin.tabs.dashboard },
    { key: "prep", label: locale === "fr" ? "🗓 Préparation 2027" : "🗓 Prep 2027" },
    { key: "comptabilite", label: locale === "fr" ? "💼 Comptabilité" : "💼 Accounting" },
    { key: "presence", label: t.admin.tabs.presence },
    { key: "documents", label: locale === "fr" ? "Ressources" : "Resources" },
    { key: "photos", label: locale === "fr" ? "Audiovisuel" : "Audio · Video" },
    { key: "videos", label: locale === "fr" ? "Vidéos" : "Videos" },
    { key: "events", label: t.admin.tabs.events },
    { key: "articles", label: t.admin.tabs.articles },
    { key: "messages", label: t.admin.tabs.messages },
    { key: "orders", label: t.admin.tabs.orders },
    { key: "donations", label: locale === "fr" ? "Dons / Mécénat" : "Donations" },
    { key: "partners", label: locale === "fr" ? "Partenaires" : "Partners" },
    { key: "intervenants", label: locale === "fr" ? "Passeurs" : "Bearers" },
    { key: "viva", label: "VIVA ARTIST" },
    { key: "hospitalite", label: locale === "fr" ? "🛖 Hospitalité" : "🛖 Hospitality" },
    { key: "accounts", label: locale === "fr" ? "Comptes" : "Accounts" },
    { key: "sauvegarde", label: locale === "fr" ? "💾 Sauvegarde" : "💾 Backup" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ background: "var(--parchment-2)" }}>
      <header className="border-b" style={{ borderColor: "var(--border)", background: "var(--parchment)" }}>
        <div className="container-prieure py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>APLEO · Back-office</div>
            <h1 className="font-display text-2xl">Prieuré saint Léonard</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm" style={{ color: "var(--muted)" }}>{admin.email}</span>
            <a href="/" target="_blank" rel="noreferrer" className="btn btn-outline" data-testid="admin-view-site"
               title={locale === "fr" ? "Ouvrir le site public dans un nouvel onglet" : "Open the public site in a new tab"}>
              {locale === "fr" ? "↗ Voir le site" : "↗ View site"}
            </a>
            <button data-testid="admin-logout" onClick={async () => { await logout(); navigate("/connexion"); }}
                    className="btn btn-outline">
              {t.admin.logout}
            </button>
          </div>
        </div>
        <div className="container-prieure flex flex-wrap gap-1 overflow-x-auto">
          {tabs.map((tt) => (
            <button
              key={tt.key}
              data-testid={`admin-tab-${tt.key}`}
              onClick={() => setTab(tt.key)}
              className="nav-link whitespace-nowrap"
              style={{
                padding: "0.75rem 1.25rem",
                background: tab === tt.key ? "var(--parchment-2)" : "transparent",
                borderBottom: tab === tt.key ? "2px solid var(--cinnabar)" : "2px solid transparent",
              }}
            >
              {tt.label}
            </button>
          ))}
        </div>
      </header>

      <main className="container-prieure py-10 space-y-8">
        {tab === "dashboard" && <Stats t={t} locale={locale} onNavigate={setTab} />}
        {tab === "prep" && <PrepDashboardTab locale={locale} onNavigate={setTab} />}
        {tab === "comptabilite" && <ComptabiliteTab locale={locale} />}
        {tab === "presence" && <PresenceTab t={t} locale={locale} />}
        {tab === "donations" && <DonationsTab locale={locale} />}
        {tab === "partners" && <PartnersTab locale={locale} />}
        {tab === "intervenants" && <IntervenantsTab locale={locale} />}
        {tab === "viva" && <VivaArtistTab />}
        {tab === "hospitalite" && <HospitaliteTab />}
        {tab === "accounts" && <AccountsTab locale={locale} />}
        {tab === "documents" && <DocumentsTab locale={locale} />}
        {tab === "photos" && <PhotosTab locale={locale} />}
        {tab === "videos" && <VideosTab locale={locale} />}
        {tab === "events" && <EventsTab t={t} locale={locale} />}
        {tab === "articles" && <ArticlesTab t={t} locale={locale} />}
        {tab === "messages" && <MessagesTab t={t} locale={locale} />}
        {tab === "orders" && <OrdersTab t={t} locale={locale} />}
        {tab === "admins" && <AdminsTab locale={locale} />}
        {tab === "sauvegarde" && <SauvegardeTab locale={locale} />}
        {/* Legacy "admins" tab kept for backwards-compat link; primary tab is now "accounts" */}
      </main>
    </div>
  );
}


// =================================================================== PROJET HUB
// Sub-tabs under Fêtes 2027 > Gestion de projet
function ProjetHub({ locale, t }) {
  const isFr = locale === "fr";
  const [proj, setProj] = useState(() => {
    return (typeof window !== "undefined"
      ? window.localStorage.getItem("admin_projet_subtab")
      : null) || "planning";
  });
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem("admin_projet_subtab", proj);
  }, [proj]);
  const [soutien, setSoutien] = useState("logistique");

  const projTabs = [
    { key: "communication", label: isFr ? "Communication" : "Communication" },
    { key: "push", label: isFr ? "Notifications push" : "Push notifications" },
    { key: "beacon", label: isFr ? "Balise groupe" : "Group beacon" },
    { key: "newsletter", label: "Newsletter" },
    { key: "finance", label: "Finance" },
    { key: "planning", label: "Planning" },
    { key: "rh", label: "RH" },
    { key: "risks", label: isFr ? "Risques" : "Risks" },
    { key: "soutien", label: "Soutien" },
  ];

  return (
    <div className="space-y-6" data-testid="projet-hub">
      <div className="flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--border)" }}>
        {projTabs.map((x) => (
          <button key={x.key} data-testid={`projet-subtab-${x.key}`}
                  onClick={() => setProj(x.key)}
                  className="nav-link"
                  style={{
                    padding: "0.5rem 1rem",
                    borderBottom: proj === x.key ? "2px solid var(--gold)" : "2px solid transparent",
                  }}>
            {x.label}
          </button>
        ))}
      </div>

      {proj === "planning" && <PlanningTab locale={locale} />}
      {proj === "finance" && <FinanceTab locale={locale} />}
      {proj === "risks" && <RisksTab locale={locale} />}
      {proj === "rh" && <RHTab locale={locale} />}
      {proj === "communication" && <CommunicationTab locale={locale} />}
      {proj === "push" && <PushBroadcastTab locale={locale} />}
      {proj === "beacon" && <BeaconTab locale={locale} />}
      {proj === "newsletter" && <NewsletterTab locale={locale} />}
      {proj === "soutien" && (
        <SoutienHub locale={locale} t={t} active={soutien} setActive={setSoutien} />
      )}
    </div>
  );
}

function SoutienHub({ locale, active, setActive }) {
  const isFr = locale === "fr";
  const tabs = [
    { key: "logistique", label: "Logistique" },
    { key: "restauration", label: "Restauration" },
    { key: "hebergement", label: isFr ? "Hébergement" : "Accommodation" },
  ];
  return (
    <div className="space-y-6" data-testid="soutien-hub">
      <div className="flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--border)" }}>
        {tabs.map((x) => (
          <button key={x.key} data-testid={`soutien-subtab-${x.key}`}
                  onClick={() => setActive(x.key)}
                  className="nav-link"
                  style={{
                    padding: "0.5rem 1rem",
                    borderBottom: active === x.key ? "2px solid var(--cinnabar)" : "2px solid transparent",
                  }}>
            {x.label}
          </button>
        ))}
      </div>
      {active === "logistique" && <LogisticsTab locale={locale} />}
      {active === "restauration" && <RestaurationTab locale={locale} />}
      {active === "hebergement" && <HebergementTab locale={locale} />}
    </div>
  );
}

function PlaceholderCard({ title, desc, kicker }) {
  return (
    <div className="admin-card space-y-3" data-testid={`placeholder-${kicker}`}>
      <div className="font-caps text-[0.6rem]" style={{ color: "var(--gold)", letterSpacing: "0.22em" }}>
        {kicker.toUpperCase()}
      </div>
      <h3 className="font-display text-2xl">{title}</h3>
      <p className="font-body" style={{ color: "var(--ink-2)" }}>{desc}</p>
      <p className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
        BACKLOG · ITÉRATION B
      </p>
    </div>
  );
}

function CommunicationPlaceholder({ isFr }) {
  return (
    <PlaceholderCard
      kicker="communication"
      title={isFr ? "Communication & Presse" : "Communication & Press"}
      desc={isFr
        ? "Stratégie de communication, planning de comm, communiqués de presse, communication interne. À développer dans la prochaine itération."
        : "Communication strategy, comms planning, press releases, internal comms. To be developed."}
    />
  );
}

function RHPlaceholder({ isFr }) {
  return (
    <PlaceholderCard
      kicker="rh"
      title={isFr ? "Ressources humaines des Fêtes" : "HR for the Feasts"}
      desc={isFr
        ? "Vue consolidée des administrateurs, organisateurs, bénévoles, artistes, conférenciers et guides associés aux Fêtes 2027 — avec filtres par rôle et statut. À développer."
        : "Consolidated view of admins, organisers, volunteers, artists, speakers and guides for Fêtes 2027 — with role and status filters. To be built."}
    />
  );
}

function RestaurationPlaceholder({ isFr }) {
  return (
    <PlaceholderCard
      kicker="restauration"
      title={isFr ? "Restauration" : "Catering"}
      desc={isFr
        ? "Gestion des repas et collations : prestataires, menus, allergies, comptages par jour. À développer."
        : "Meal and refreshment management: providers, menus, allergies, per-day counts."}
    />
  );
}

function HebergementPlaceholder({ isFr }) {
  return (
    <PlaceholderCard
      kicker="hebergement"
      title={isFr ? "Hébergement" : "Accommodation"}
      desc={isFr
        ? "Inventaire des hébergements (chez l'habitant, gîtes, congrégations, hôtels, tentes), affectation des participants. À développer."
        : "Accommodation inventory (homestay, gîtes, communities, hotels, tents) and participant matching."}
    />
  );
}
